import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./EditItemPhoto.sass";

import { FileInputButton } from "_components";
import { FileUpload, useFileUpload } from "./FileUpload";

import { Photo } from "_types/item";
import { resizeUrl } from "_utils/image";
import { useDrag, useDrop } from "react-dnd";
import arrayMove from "array-move";

type PhotoValidationError = "incorrect format" | "file too large";

async function validatePhoto(file): Promise<PhotoValidationError | null> {
  if ((file.type !== 'image/jpeg') && (file.type !== 'image/png')) return "incorrect format";
  if (file.size > 8 * 1024 * 1024) return "file too large";
  return null;
}

interface GalleryTileProps extends React.BaseHTMLAttributes<HTMLDivElement> {
  index: number;
  photo: Photo;
  selected: boolean;
  reorder: (oldIndex: number, newIndex: number) => void;
  onShowCarousel: () => void;
}

const GalleryTile: React.FC<GalleryTileProps> = (
  { index, photo, selected, reorder, onShowCarousel, ...otherProps }
) => {
  const [{ isDragging }, drag] = useDrag({
    item: { type: "galleryPhoto", index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop<{ type: string; index: number; }, void, {isOver: boolean}>({
    accept: "galleryPhoto",
    drop: (item) => reorder(item.index, index),
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  });

  // TODO: the top-level div should be both the drag source and the drop target
  return <div ref={drop}
              className={`gallery-tile${selected ? ' selected' : ''}`}
              style={{
                opacity: isDragging ? 0.5 : 1,
                borderLeft: isOver ? "thick solid #1c1e27" : ""
              }}
              {...otherProps}
  >
    <img ref={drag}
         src={resizeUrl(photo.url, {width: 150, height: 150})}
         alt=""
         tabIndex={-1}
         onDoubleClick={onShowCarousel}
    />
  </div>
}

interface GalleryProps {
  collection: Photo[];
  order: string[];
  reorder: (oldIndex: number, newIndex: number) => void;
  selection: string | null;
  setSelection: (selection: string | null) => void;
  onShowCarousel: () => void;
}

const Gallery: React.FC<GalleryProps> = ({ collection, order, reorder, selection, setSelection, onShowCarousel }) => (
  <div
    className='gallery'
    onClick={() => setSelection(null)}
  >
    {(order.length === 0) && <p className='placeholder-large'>The gallery is empty.</p>}
    {order.map((uuid, index) => <GalleryTile
      key={uuid} index={index}
      photo={collection.find(i => i.uuid === uuid)!}
      selected={uuid === selection}
      reorder={reorder}
      onShowCarousel={onShowCarousel}
      onClick={(e) => {e.stopPropagation(); setSelection(uuid)}}
    />)}
  </div>
)

interface EditItemPhotoProps {
  collection: Photo[];
  order: string[];
  setCollection: React.Dispatch<React.SetStateAction<Photo[]>>;
  setOrder: React.Dispatch<React.SetStateAction<string[]>>;
  endpoint: string;
}

export const EditItemPhoto: React.FC<EditItemPhotoProps> = props => {
  // Helpers
  const append = (photo: Photo) => {
    props.setCollection(collection => [...collection, photo]);
    props.setOrder(order => [...order, photo.uuid]);
  }
  const reorder = (oldIndex, newIndex) => props.setOrder(order => arrayMove(order, oldIndex, newIndex));
  const removeSelection = () => props.setOrder(order => order.filter(uuid => uuid !== selection));

  // Hooks
  const [selection, setSelection] = useState<string | null>(null);
  // eslint-disable-next-line
  const [showCarousel, setShowCarousel] = useState(false);
  const { fileUploadStates, addFiles, tryAgain } = useFileUpload(validatePhoto, props.endpoint, append);

  return <div className='section edit-item-photo'>
    <p className="section-name">Gallery</p>
    <div className='action-buttons'>
      <FileInputButton contextualClass='primary'
                       accept='image/jpeg,image/png' multiple
                       onSelectFiles={addFiles}
      >
        <FontAwesomeIcon icon='plus' />&ensp;Add photos
      </FileInputButton>

      <button className='btn btn-danger' disabled={selection === null} onClick={e => { e.preventDefault(); removeSelection(); }}>
        <FontAwesomeIcon icon='trash-alt' />&ensp;Remove photo
      </button>
    </div>
    {/*onUpload={onGalleryUpload(photos, onChange)}*/}
    {(fileUploadStates.length !== 0) && <FileUpload fileUploadStates={fileUploadStates} tryAgain={tryAgain} />}
    <Gallery collection={props.collection}
             order={props.order} reorder={reorder}
             selection={selection} setSelection={setSelection}
             onShowCarousel={() => setShowCarousel(true)}
    />
    {/*TODO: react-photoswipe somehow causes the page to reload or crash, replace with a better-maintained library*/}
    {/*<ImageCarousel photos={photoList({order, collection})}*/}
    {/*               isOpen={showCarousel}*/}
    {/*               index={selection || undefined}*/}
    {/*               onClose={() => setShowCarousel(false)}*/}
    {/*/>*/}
  </div>
}
